/** On-demand / Services master types (mirrors backend on_demand_masters group). */
export const ON_DEMAND_MASTER_TYPES = [
  { key: 'service_categories', label: 'Service Categories' },
  { key: 'skills', label: 'Skills' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'experience_levels', label: 'Experience Levels' },
  { key: 'pricing_templates', label: 'Pricing Templates' },
  { key: 'visit_charges', label: 'Visit Charges' },
  { key: 'cancellation_reasons', label: 'Cancellation Reasons' },
] as const;

export type OnDemandMasterTypeKey = (typeof ON_DEMAND_MASTER_TYPES)[number]['key'];

/** Short helper copy shown under master type headers in Services Masters. */
export const ON_DEMAND_MASTER_HELPERS: Partial<Record<OnDemandMasterTypeKey, string>> = {
  service_categories:
    'Primary categories sorted by display order. Use the eye icon to manage secondary (sub) categories. Add Primary requires name, code, icon, display order, and accent color. Delete permanently removes a category. Use the list status toggle to activate or deactivate.',
  skills:
    'Provider skill tags sorted by display order. Deprecated skills are hidden and excluded from new provider/package selection. Skill names must be unique. Delete permanently removes unused skills (blocked when packages or providers still reference them). Use Seed masters to refresh the curated set and clean duplicates.',
  pricing_templates:
    'Pricing template = the service package pricing model blueprint (fixed / hourly / per visit amounts used when defining packages — base labour price structure).',
  visit_charges:
    'Visit charges = the call-out / doorstep fee charged for the provider visiting the customer location (often added on top of base package price).',
};

/** Master types that auto-generate code from display name (create flow). */
export const ON_DEMAND_AUTO_CODE_TYPES = new Set<string>(
  ON_DEMAND_MASTER_TYPES.map((t) => t.key),
);

/** Hide Active/Inactive toggle on Add/Edit — list toggle owns status. */
export const ON_DEMAND_HIDE_STATUS_TOGGLE_TYPES = new Set<string>([
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
]);

/** Auto-assign next display order on create. */
export const ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES = new Set<string>([
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
]);

/** Whole-number sort order only. */
export const ON_DEMAND_INTEGER_SORT_ORDER_TYPES = new Set<string>([
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
]);

/**
 * Unique sort order within sibling scope.
 * service_categories: unique among roots, or among children of the same parent.
 */
export const ON_DEMAND_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
]);

export const ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'service_categories',
]);

/** Block repeated characters/words in description. */
export const ON_DEMAND_DESCRIPTION_VALIDATED_TYPES = new Set<string>([
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
]);

/** Uppercase SKU-style code from package name (e.g. "AC Service Basic" → "AC_SERVICE_BASIC"). */
export function slugifyPackageCode(name: string, categoryCode?: string): string {
  const fromName = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  if (!fromName) return '';
  if (!categoryCode) return fromName;
  const cat = categoryCode.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
  if (!cat || fromName.startsWith(cat)) return fromName;
  if (fromName.length >= 6) return fromName;
  return `${cat}_${fromName}`;
}

/** Same as package slug without category prefix — used for master codes. */
export function slugifyMasterCode(name: string): string {
  return slugifyPackageCode(name);
}

/** Ensure code does not collide with existing package codes. */
export function uniquePackageCode(base: string, existingCodes: string[]): string {
  if (!base) return '';
  const set = new Set(existingCodes.map((c) => c.toUpperCase()));
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}
